import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import store from "store";
import Socket from "utils/socket/public";
import { get_depth, get_tradeRecent } from "api/v4/market";
import useAxiosCancelFun from "hooks/useAxiosCancelFun";
import useTimeout from "hooks/useTimeout";
import XtLoading from "@/components/Xt/Loading";
import XtFontScale from "@/components/Xt/fontScale";
import XtScrollLay from "@/components/Xt/scroll/lay";
import useCoinMemo from "@/components/Pages/Trade/_hook/useCoinMemo";
import { filterBigNumThousands } from "utils/method";
import styles from "./index.module.scss";

const { useTranslation } = Hooks;
const { Big } = Util;

interface WsDepthProps {
  s: string; //交易对
  a?: Array<Array<string>>; //卖盘数组
  b?: Array<Array<string>>; //买盘数组
  fi: number; //firstUpdateId 等于上一次推送的lastUpdateId + 1
  i: number; //lastUpdateId
}

interface apiDepthProps {
  asks: Array<Array<string>>;
  bids: Array<Array<string>>;
  lastUpdateId: number;
}

/** 仅订阅盘口，cfdView 更新时只有盘口区重渲染 */
const OrderBookDepthSection: React.FC<{ type: "ask" | "bid" }> = observer(({ type }) => {
  const depth = type === "ask" ? store.cfdView.depthAsks : store.cfdView.depthBids;
  const depthDisplay = useMemo(() => (depth || []).slice(0, 50), [depth]);
  const maxAmount = useMemo(() => {
    let max = 0;
    (depth || []).slice(0, 50).forEach((item: any[]) => {
      if (+item[1] > max) max = +item[1];
    });
    return max;
  }, [depth]);
  const openOrderObj = useMemo(() => ({}), []);

  return (
    <XtScrollLay
      className={type === "ask" ? styles.scrollWindowAsk : styles.scrollWindowBid}
      height={20}
      dataAry={depthDisplay}
      isReverse={type === "ask"}
    >
      {depthDisplay.map((ary: any[]) => {
        const docFormat = {
          priceLab: filterBigNumThousands(ary[0]),
          amountLab: filterBigNumThousands(ary[1]),
        };
        const handleClick = () => {
          store.trade.updateState({
            tradeRecentOnce: { p: ary[0], q: ary[1], total: ary[2], isClick: true },
          });
        };
        const barStyle: any = maxAmount ? { width: (+ary[1] / maxAmount) * 100 + "%" } : {};
        return (
          <div
            key={ary[0]}
            onClick={handleClick}
            className={cx(styles.orderItem, type === "ask" ? styles.ask : styles.bid, { [styles.atv]: !!openOrderObj[ary[0]] })}
          >
            <div className={styles.bar} style={barStyle} />
            <div className={styles.itemContent}>
              <XtFontScale className={styles.price}>{docFormat.priceLab}</XtFontScale>
              <XtFontScale className={styles.amount}>{docFormat.amountLab}</XtFontScale>
            </div>
          </div>
        );
      })}
    </XtScrollLay>
  );
});

/** 仅订阅 ticker/成交，cfdView 更新时只有价格行重渲染 */
const OrderBookCurrentPrice = observer(function OrderBookCurrentPrice() {
  const { cfdTicker, tradeRecent } = store.cfdView;
  const currentPriceLab = useMemo(() => {
    if (cfdTicker?.c) return filterBigNumThousands(cfdTicker.c);
    if (tradeRecent?.p) return filterBigNumThousands(tradeRecent.p);
    return "--";
  }, [cfdTicker, tradeRecent]);
  const usdPriceLab = useMemo(() => {
    const price = cfdTicker?.c || tradeRecent?.p;
    if (!price) return "--";
    const priceNum = Big(price);
    return priceNum.gt(0) ? `$ ${filterBigNumThousands(priceNum.toFixed(2))}` : "--";
  }, [cfdTicker, tradeRecent]);

  return (
    <div className={styles.currentPrice}>
      <b className={styles.priceText}>{currentPriceLab}</b>
      <div className={styles.usdPrice}>{usdPriceLab}</div>
    </div>
  );
});

const OrderBook: React.FC = () => {
  const t = useTranslation();
  const { name, currentConfig } = store.market;
  const { coinQuantityUpperCase, coinPriceUpperCase } = useCoinMemo();

  // 从 name 计算 coinBuy 和 coinSell，和现货保持一致
  const coinSell = useMemo(() => {
    return (name.split("_")[0] || "").toUpperCase();
  }, [name]);
  const coinBuy = useMemo(() => {
    return (name.split("_")[1] || "").toUpperCase();
  }, [name]);

  const [loading, setLoading] = useState<boolean>(true);
  const setLoadingFalse = useCallback(() => setLoading(false), []);
  const [timeoutLoading, timeoutLoadingClear] = useTimeout(setLoadingFalse, { ms: 2000 });

  const isCacheWs = useRef<boolean>(true); //是否缓存ws
  const wsCacheAry = useRef<WsDepthProps[]>([]); //ws缓存集合
  const wsLatest = useRef<WithUndefined<WsDepthProps>>(); //最新推送的ws
  const isHasReady = useRef<boolean>(false);

  // 节流写 store，200ms 进一步减轻卡顿
  const THROTTLE_MS = 200;
  const depthAsksRef = useRef<Array<Array<string>> | null>(null);
  const depthBidsRef = useRef<Array<Array<string>> | null>(null);
  const tradeRecentRef = useRef<any>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const depthLimit = 20; // depth 通道与 REST 统一使用 20 档
  const apiReqDepthArg = useMemo(() => {
    // 直接使用 name，带 _cfd 的就带 _cfd，不需要删除
    return {
      fn: get_depth,
      config: {
        params: {
          symbol: name,
          limit: depthLimit,
        },
      },
    };
  }, [name]);
  const apiReqDepth = useAxiosCancelFun(apiReqDepthArg);

  // 获取最近成交数据
  const apiReqTradeRecentArg = useMemo(() => {
    // 直接使用 name，带 _cfd 的就带 _cfd，不需要删除
    return {
      fn: get_tradeRecent,
      config: {
        params: {
          symbol: name,
          limit: 100,
        },
      },
    };
  }, [name]);
  const apiReqTradeRecent = useAxiosCancelFun(apiReqTradeRecentArg);

  const depthDataReadyRef = useRef(false);
  const tradeRecentReadyRef = useRef(false);

  const mixDepthArray = useCallback((array, newArray) => {
    const retAry = [...array];
    if (!newArray || !newArray.length) return retAry;

    newArray.map((item) => {
      const index = retAry.findIndex((val) => val[0] === item[0]);
      if (index >= 0) {
        if (+item[1] === 0) {
          retAry.splice(index, 1);
        } else {
          retAry[index] = item;
        }
      } else {
        if (+item[1] > 0) {
          retAry.push(item);
        }
      }
    });
    return retAry;
  }, []);

  const sumTotal = useCallback((array: Array<Array<string>>, isReverse?: boolean, dp?) => {
    const retAry: Array<Array<string>> = [];
    let total = Big(0);
    const ary = isReverse ? [...array].reverse() : array;

    ary.map((item) => {
      total = total.plus(item[1]);
      retAry.push([item[0], item[1], total.toFixed(dp)]);
    });

    return isReverse ? retAry.reverse() : retAry;
  }, []);

  const dataUpdate = useCallback((data: { asks?: Array<Array<string>>; bids?: Array<Array<string>> }, isFull?: boolean) => {
    const { asks, bids } = data;
    let retAsks, retBids;
    if (isFull) {
      retAsks = asks || [];
      retBids = bids || [];
    } else {
      retAsks = mixDepthArray(store.cfdView.depthAsks, asks);
      retBids = mixDepthArray(store.cfdView.depthBids, bids);
    }

    retAsks.sort((a, b) => b[0] - a[0]); //从大到小排序
    retBids.sort((a, b) => b[0] - a[0]); //从大到小排序

    const quantityPrecision = currentConfig?.quantityPrecision || 8;
    retAsks = sumTotal(retAsks, true, quantityPrecision);
    retBids = sumTotal(retBids, false, quantityPrecision);

    store.cfdView.updateState({
      depthAsks: retAsks,
      depthBids: retBids,
    });
    depthAsksRef.current = retAsks;
    depthBidsRef.current = retBids;
  }, []); //数据更新

  const flushToStore = useCallback(() => {
    flushTimerRef.current = null;
    const asks = depthAsksRef.current;
    const bids = depthBidsRef.current;
    if (asks && bids) {
      store.cfdView.updateState({ depthAsks: asks, depthBids: bids });
    }
    const trade = tradeRecentRef.current;
    if (trade !== null && trade !== undefined) {
      store.cfdView.updateState({ tradeRecent: trade });
      tradeRecentRef.current = null;
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(flushToStore, THROTTLE_MS);
  }, [flushToStore]);

  const spliceData = useCallback((data: WithUndefined<apiDepthProps>): true | undefined => {
    let index = 0;
    if (!data) return true;
    if (data && data.lastUpdateId) {
      if (!wsCacheAry.current.length) {
        wsLatest.current = {
          i: data.lastUpdateId,
          fi: data.lastUpdateId,
          s: name,
        };
      } else {
        let doc, isOK;
        for (let i = 0; i < wsCacheAry.current.length; i++) {
          doc = wsCacheAry.current[i];
          if (doc.fi - 1 === data.lastUpdateId) {
            index = i;
            isOK = true;
            break;
          }
          if (doc.i === data.lastUpdateId) {
            index = i + 1;
            isOK = true;
            break;
          }
        }
        if (!isOK) {
          if (!isHasReady.current) {
            //如果从没有正常连续情况
            console.log("从没有正常连续情况，使用接口数据展示");
            isHasReady.current = true;
            dataUpdate(data, true);
          }

          return true;
        }
      }
    }

    let asks: Array<Array<string>> = [],
      bids: Array<Array<string>> = [];
    if (data) {
      asks = data.asks;
      bids = data.bids;
    }

    for (let i = index; i < wsCacheAry.current.length; i++) {
      asks = mixDepthArray(asks, wsCacheAry.current[i].a);
      bids = mixDepthArray(bids, wsCacheAry.current[i].b);
    }

    dataUpdate({ asks, bids }, true);

    isCacheWs.current = false;
    wsCacheAry.current = [];

    refRetryCount.current = 0;
    isHasReady.current = true;
  }, []); //数据拼接

  const refRetryCount = useRef<number>(0);
  const refRetryTimeout = useRef<number>();
  const retry = useCallback(
    (data?: WsDepthProps) => {
      isCacheWs.current = true;
      wsCacheAry.current = data ? [data] : [];
      wsLatest.current = data;
      console.log("depth_update@ retry", refRetryTimeout.current);

      if (refRetryTimeout.current) return;
      refRetryCount.current = refRetryCount.current + 1;
      refRetryTimeout.current = window.setTimeout(() => {
        refRetryTimeout.current = 0;
        console.log("depth_update@ retry start");
        apiReqDepth(({ err, data, config }) => {
          console.log("depth_update@ config", config);
          // 直接比较，不需要删除 _cfd 后缀
          const configSymbol = config?.params?.symbol || "";
          if (configSymbol !== store.market.name) {
            console.log("depth_update@ config.params.symbol !== name", configSymbol, store.market.name);
            return;
          }
          const rst = spliceData(err ? undefined : data);
          rst && retry();
        });
      }, (refRetryCount.current > 3 ? 3 : refRetryCount.current) * 200);
    },
    [apiReqDepth, spliceData]
  );

  const wsCallback = useCallback(
    (data: WsDepthProps) => {
      if (data.s !== store.market.name) {
        console.log("depth_update@ name !== data.s", store.market.name, data.s);
        return;
      }
      if (wsLatest.current && wsLatest.current.i !== data.fi - 1) {
        console.log("ws 不连续=====", { name, wsLatest: wsLatest.current, ws: data });
        return retry(data);
      }
      wsLatest.current = data;
      if (isCacheWs.current) return wsCacheAry.current.push(data);

      const baseAsks = depthAsksRef.current ?? store.cfdView.depthAsks ?? [];
      const baseBids = depthBidsRef.current ?? store.cfdView.depthBids ?? [];
      let retAsks = mixDepthArray(baseAsks, data.a);
      let retBids = mixDepthArray(baseBids, data.b);
      retAsks.sort((a, b) => Number(b[0]) - Number(a[0]));
      retBids.sort((a, b) => Number(b[0]) - Number(a[0]));
      const quantityPrecision = currentConfig?.quantityPrecision || 8;
      retAsks = sumTotal(retAsks, true, quantityPrecision);
      retBids = sumTotal(retBids, false, quantityPrecision);
      depthAsksRef.current = retAsks;
      depthBidsRef.current = retBids;
      scheduleFlush();
    },
    [mixDepthArray, sumTotal, scheduleFlush]
  );

  // depth@ 全量快照：写入 ref，由定时器节流刷到 store
  const wsCallbackDepth = useCallback(
    (data: { s?: string; asks?: Array<Array<string>>; bids?: Array<Array<string>>; a?: Array<Array<string>>; b?: Array<Array<string>> }) => {
      if (data.s !== store.market.name) return;
      const asks = (data.asks ?? data.a ?? []).slice();
      const bids = (data.bids ?? data.b ?? []).slice();
      asks.sort((a, b) => Number(b[0]) - Number(a[0]));
      bids.sort((a, b) => Number(b[0]) - Number(a[0]));
      const quantityPrecision = currentConfig?.quantityPrecision || 8;
      depthAsksRef.current = sumTotal(asks, true, quantityPrecision);
      depthBidsRef.current = sumTotal(bids, false, quantityPrecision);
      scheduleFlush();
    },
    [sumTotal, scheduleFlush]
  );

  useEffect(() => {
    console.log("depth_update@ add", name);
    const msg = { event: `depth_update@${name}` };
    const msgDepth = { event: `depth@${name},${depthLimit}` };

    Socket.addChannel(msg, wsCallback);
    Socket.addChannel(msgDepth, wsCallbackDepth);
    const cancelFun = apiReqDepth(({ err, data, config }) => {
      console.log("depth_update@ config", config);
      if (store.market.name !== name) return;
      // 直接比较，不需要删除 _cfd 后缀
      const configSymbol = config?.params?.symbol || "";
      if (configSymbol !== name) {
        console.log("depth_update@ config.params.symbol !== name", configSymbol, name);
        return;
      }
      const rst = spliceData(err ? undefined : data);
      rst && retry();
      depthDataReadyRef.current = true;
      if (tradeRecentReadyRef.current) {
        timeoutLoadingClear();
        setLoading(false);
      }
      timeoutLoading();
    });

    return () => {
      console.log("depth_update@ remove", name);
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      depthAsksRef.current = null;
      depthBidsRef.current = null;
      tradeRecentRef.current = null;
      depthDataReadyRef.current = false;
      tradeRecentReadyRef.current = false;
      cancelFun && cancelFun.current && cancelFun.current();
      timeoutLoadingClear();
      isCacheWs.current = true;
      wsCacheAry.current = [];
      wsLatest.current = undefined;

      setLoading(true);
      Socket.removeChannel(msg);
      Socket.removeChannel(msgDepth);
      store.cfdView.updateState({ depthAsks: undefined, depthBids: undefined });
    };
  }, [name, wsCallback, wsCallbackDepth]);

  // 监听 trade@ WebSocket 频道，写入 ref，由定时器节流刷到 store
  const wsCallbackTrade = useCallback(
    (data) => {
      if (data.s !== store.market.name) {
        console.log("trade@ name !== data.s", store.market.name, data.s);
        return;
      }
      tradeRecentRef.current = data;
      scheduleFlush();
    },
    [scheduleFlush]
  );

  useEffect(() => {
    const msg = { event: `trade@${name}` };
    const cancelFun = apiReqTradeRecent(({ err, data, config }) => {
      if (store.market.name !== name) return;
      // 直接比较，不需要删除 _cfd 后缀
      if (!(config && config.params && config.params.symbol === name)) {
        return;
      }
      const resData = err ? [] : data;
      store.cfdView.updateState({ tradeRecent: resData[0] || null });
      store.trade.updateState({ tradeRecentOnce: resData[0] || null });
      tradeRecentReadyRef.current = true;
      if (depthDataReadyRef.current) {
        timeoutLoadingClear();
        setLoading(false);
      }
      Socket.addChannel(msg, wsCallbackTrade);
    });

    return () => {
      cancelFun && cancelFun.current && cancelFun.current();
      Socket.removeChannel(msg);
      store.cfdView.updateState({ tradeRecent: undefined });
      store.trade.updateState({ tradeRecentOnce: undefined });
    };
  }, [name, apiReqTradeRecent, wsCallbackTrade]);

  // 不限制数量，让 XtScrollLay 根据容器高度自动计算显示数量
  // 传入足够多的数据，确保能填满容器
  return (
    <div className={styles.orderBook}>
      <div className={styles.content}>
        {loading && <XtLoading />}
        {!loading && (
          <>
            {/* 标题行 */}
            <div className={styles.header}>
              <div className={styles.priceLabel}>
                <span>{t("cfd.price")}</span>
                <span>({coinPriceUpperCase})</span>
              </div>
              <div className={styles.amountLabel}>
                <span>{t("cfd.quantity")}</span>
                <span>(Cont)</span>
              </div>
            </div>

            {/* 卖盘 - 仅盘口区订阅 cfdView.depthAsks */}
            <OrderBookDepthSection type="ask" />

            {/* 当前价格 - 仅价格行订阅 cfdView.cfdTicker/tradeRecent */}
            <OrderBookCurrentPrice />

            {/* 买盘 - 仅盘口区订阅 cfdView.depthBids */}
            <OrderBookDepthSection type="bid" />
          </>
        )}
      </div>
    </div>
  );
};

export default observer(OrderBook);

